import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { X, Info, AlertTriangle, CheckCircle, AlertCircle, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { PlatformBanner as PlatformBannerType } from "@shared/schema";

const SESSION_KEY = "dismissed-banners";

function getDismissedIds(): Set<string> {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch {
    return new Set();
  }
}

function dismissBanner(id: string) {
  const dismissed = getDismissedIds();
  dismissed.add(id);
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(Array.from(dismissed)));
}

const typeConfig: Record<string, { bg: string; border: string; text: string; iconBg: string; icon: typeof Info }> = {
  info: {
    bg: "bg-blue-600 dark:bg-blue-700",
    border: "border-blue-700 dark:border-blue-600",
    text: "text-white",
    iconBg: "bg-blue-500 dark:bg-blue-600",
    icon: Info,
  },
  warning: {
    bg: "bg-amber-500 dark:bg-amber-600",
    border: "border-amber-600 dark:border-amber-500",
    text: "text-white",
    iconBg: "bg-amber-400 dark:bg-amber-500",
    icon: AlertTriangle,
  },
  success: {
    bg: "bg-emerald-600 dark:bg-emerald-700",
    border: "border-emerald-700 dark:border-emerald-600",
    text: "text-white",
    iconBg: "bg-emerald-500 dark:bg-emerald-600",
    icon: CheckCircle,
  },
  error: {
    bg: "bg-red-600 dark:bg-red-700",
    border: "border-red-700 dark:border-red-600",
    text: "text-white",
    iconBg: "bg-red-500 dark:bg-red-600",
    icon: AlertCircle,
  },
};

export function PlatformBanner() {
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    setDismissedIds(getDismissedIds());
  }, []);

  const { data: banners = [] } = useQuery<PlatformBannerType[]>({
    queryKey: ["/api/banners/active"],
    refetchInterval: 60000,
  });

  const visibleBanners = banners.filter((b) => !dismissedIds.has(b.id));

  if (visibleBanners.length === 0) return null;

  function handleDismiss(id: string) {
    dismissBanner(id);
    setDismissedIds((prev) => {
      const next = new Set(Array.from(prev));
      next.add(id);
      return next;
    });
  }

  return (
    <div className="space-y-0">
      {visibleBanners.map((banner) => {
        const config = typeConfig[banner.type] || typeConfig.info;
        const IconComponent = config.icon;
        return (
          <div
            key={banner.id}
            className={`${config.bg} ${config.border} ${config.text} border-b px-4 py-3.5`}
          >
            <div className="container mx-auto flex items-center gap-3">
              <div className={`${config.iconBg} rounded-full p-1.5 shrink-0`}>
                <IconComponent className="h-4 w-4" />
              </div>
              <p className="flex-1 text-sm font-medium leading-snug">{banner.message}</p>
              {banner.linkUrl && (
                <a
                  href={banner.linkUrl}
                  {...(/^https?:\/\//.test(banner.linkUrl)
                    ? { target: "_blank", rel: "noopener noreferrer" }
                    : {})}
                  className="shrink-0 text-sm font-semibold underline underline-offset-2 flex items-center gap-1 opacity-90"
                >
                  {banner.linkText || "Learn more"}
                  <ArrowRight className="h-3.5 w-3.5" />
                </a>
              )}
              <button
                className="shrink-0 rounded-full p-1 opacity-70 transition-opacity"
                onClick={() => handleDismiss(banner.id)}
                aria-label="Dismiss"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
